import React, { useState, useCallback } from 'react';
import {
  Modal,
  Form,
  Button,
  Icon,
  Message,
  Table,
  Label,
  TextArea,
  Dropdown,
} from 'semantic-ui-react';
import { API, showError, showInfo, showSuccess } from '../helpers';
import { parseSiteConfig, configToChannels } from '../helpers/configParser';
import { CHANNEL_OPTIONS } from '../constants';

/**
 * 站点配置导入弹窗组件
 * 支持解析配置文本并批量导入渠道，支持一键拉取上游模型列表
 * @param {Object} props - 组件属性
 * @param {boolean} props.open - 弹窗是否打开
 * @param {Function} props.onClose - 关闭回调
 * @param {Function} props.onImportSuccess - 导入成功回调
 * 修改时间：2026-06-17
 */
const ImportSiteModal = ({ open, onClose, onImportSuccess }) => {
  const [configText, setConfigText] = useState('');
  const [parsedConfig, setParsedConfig] = useState(null);
  const [channels, setChannels] = useState([]);
  const [existingChannels, setExistingChannels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState('');
  const [groupName, setGroupName] = useState('default');
  const [channelType, setChannelType] = useState(8); // 默认使用自定义渠道
  // 一键拉取模型相关状态
  const [fetchingModels, setFetchingModels] = useState(false);

  /**
   * 一键拉取上游模型列表
   * 从已解析配置中随机选取一个有 key 的凭证，拉取所有可用模型
   * 拉取结果与原配置模型合并并去重
   */
  const handleFetchModels = useCallback(async () => {
    if (!parsedConfig) {
      showError('请先解析配置');
      return;
    }
    // 筛选有 apiKey 的凭证
    const credsWithKey = parsedConfig.credentials.filter(
      (c) => c.apiKey && c.apiKey.trim()
    );
    if (credsWithKey.length === 0) {
      showError('没有可用的 API Key，无法拉取模型');
      return;
    }
    // 随机选取一个凭证
    const randomIdx = Math.floor(Math.random() * credsWithKey.length);
    const cred = credsWithKey[randomIdx];

    try {
      setFetchingModels(true);
      const res = await API.get('/api/channel/fetch_models', {
        params: { base_url: parsedConfig.baseUrl, key: cred.apiKey },
      });
      if (res.data.success) {
        const fetchedModels = res.data.data || [];
        if (fetchedModels.length === 0) {
          showError('上游未返回任何模型');
          return;
        }
        // 合并拉取的模型与原配置模型并去重
        const mergedModels = Array.from(
          new Set([...parsedConfig.models, ...fetchedModels])
        );
        // 更新已解析配置的模型列表
        const newConfig = { ...parsedConfig, models: mergedModels };
        setParsedConfig(newConfig);
        // 同步更新渠道数据中的模型
        setChannels(configToChannels(newConfig, groupName, channelType));
        const newCount = mergedModels.length - parsedConfig.models.length;
        showSuccess(
          `拉取成功，新增 ${newCount} 个模型，合并后共 ${mergedModels.length} 个`
        );
      } else {
        showError(res.data.message || '拉取模型失败');
      }
    } catch (err) {
      showError(err.message || '拉取模型失败');
    } finally {
      setFetchingModels(false);
    }
  }, [parsedConfig, groupName, channelType]);

  /**
   * 解析配置文本
   * 将用户输入的文本解析为渠道配置
   */
  const handleParse = useCallback(async () => {
    try {
      setError('');
      setLoading(true);

      // 解析配置文本
      const config = parseSiteConfig(configText);
      setParsedConfig(config);

      // 转换为渠道数据，传入分组名称和渠道类型
      const newChannels = configToChannels(config, groupName, channelType);
      const duplicateCount = config.credentials.length - newChannels.length;
      if (duplicateCount > 0) {
        showInfo(`已自动过滤 ${duplicateCount} 个重复 API Key`);
      }
      setChannels(newChannels);

      // 获取现有渠道用于覆盖模式判断
      const res = await API.get('/api/channel/?p=0&size=1000');
      if (res.data.success) {
        setExistingChannels(res.data.data || []);
      }

      showSuccess('解析成功，请检查导入内容');
    } catch (err) {
      setError(err.message || '解析失败');
      setParsedConfig(null);
      setChannels([]);
    } finally {
      setLoading(false);
    }
  }, [configText, groupName, channelType]);

  /**
   * 删除待导入列表中的单个渠道
   * @param {number} index - 待删除渠道在列表中的索引
   */
  const handleRemoveChannel = useCallback((index) => {
    setChannels((prev) => prev.filter((_, i) => i !== index));
  }, []);

  /**
   * 关闭弹窗并重置状态
   */
  const handleClose = useCallback(() => {
    setConfigText('');
    setParsedConfig(null);
    setChannels([]);
    setExistingChannels([]);
    setError('');
    onClose();
  }, [onClose]);

  /**
   * 执行导入操作
   * 覆盖模式：按标题名分组，只删除同名同组的旧渠道，再批量创建新渠道
   * 避免清空 default 等大分组，同时解决更新时匹配错误导致修改不生效的问题
   */
  const handleImport = useCallback(async () => {
    if (channels.length === 0) {
      showError('没有可导入的渠道');
      return;
    }

    try {
      setImporting(true);
      let deleteCount = 0;
      let createCount = 0;
      let failCount = 0;

      const channelsToCreate = channels.map((ch) => ({
        ...ch,
        group: 'default',
        name: ch.name && ch.name.trim() ? ch.name.trim() : '导入站点',
      }));

      // 1. 先删除同名同组的旧渠道（覆盖模式）
      const deletedIds = new Set();
      for (const ch of channelsToCreate) {
        const matches = existingChannels.filter(
          (ex) =>
            ex.group === ch.group &&
            ex.name === ch.name &&
            !deletedIds.has(ex.id)
        );
        for (const ex of matches) {
          try {
            const res = await API.delete(`/api/channel/${ex.id}`);
            if (res.data.success) {
              deletedIds.add(ex.id);
              deleteCount++;
            } else {
              failCount++;
              console.error('删除旧渠道失败:', ex.id, res.data.message);
            }
          } catch (err) {
            console.error('删除旧渠道失败:', ex.id, err);
            failCount++;
          }
        }
      }

      // 2. 批量创建新渠道
      for (const channel of channelsToCreate) {
        try {
          const createData = {
            name: channel.name,
            type: channel.type,
            key: channel.key,
            base_url: channel.base_url,
            models: channel.models,
            group: channel.group,
          };

          const res = await API.post('/api/channel/', createData);
          if (res.data.success) {
            createCount++;
          } else {
            failCount++;
          }
        } catch (err) {
          console.error('创建渠道失败:', channel.name, err);
          failCount++;
        }
      }

      // 显示结果
      const messages = [];
      if (deleteCount > 0) messages.push(`覆盖 ${deleteCount} 个旧渠道`);
      if (createCount > 0) messages.push(`新增 ${createCount} 个`);
      if (failCount > 0) messages.push(`失败 ${failCount} 个`);

      showSuccess(`导入完成：${messages.join('，')}`);

      // 重置状态并关闭弹窗
      handleClose();
      if (onImportSuccess) {
        onImportSuccess();
      }
    } catch (err) {
      showError(err.message || '导入失败');
    } finally {
      setImporting(false);
    }
  }, [channels, existingChannels, groupName, onImportSuccess, handleClose]);

  return (
    <Modal
      open={open}
      onClose={handleClose}
      size='large'
      closeIcon
      className='studio-modal studio-import-site-modal'
    >
      <Modal.Header
        className='studio-import-site-modal__header'
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: '22px 24px',
          color: '#18181b',
          borderBottom: '1px solid rgba(24, 24, 27, 0.08)',
          background:
            'radial-gradient(circle at 90% 0%, rgba(40, 125, 120, 0.09), transparent 35%), linear-gradient(120deg, rgba(79, 99, 168, 0.08), transparent 55%), #fafafa',
          fontSize: 20,
          fontWeight: 800,
          letterSpacing: '-0.02em',
        }}
      >
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 36,
            height: 36,
            flex: '0 0 36px',
            color: '#fff',
            border: '1px solid #18181b',
            borderRadius: 11,
            background: 'linear-gradient(145deg, #303036, #09090b)',
            boxShadow: '0 8px 20px rgba(24, 24, 27, 0.16)',
          }}
        >
          <Icon name='cloud download' style={{ margin: 0 }} />
        </span>
        <span>
          <small
            style={{
              display: 'block',
              marginBottom: 3,
              color: '#4f63a8',
              fontSize: 10,
              fontWeight: 800,
              letterSpacing: '0.14em',
            }}
          >
            AI STUDIO
          </small>
          一键导入站点配置
        </span>
      </Modal.Header>

      <Modal.Content
        scrolling
        className='studio-import-site-modal__content'
        style={{ padding: 24, background: '#fbfbfc' }}
      >
        {error && (
          <Message negative>
            <Message.Header>错误</Message.Header>
            <p>{error}</p>
          </Message>
        )}

        {!parsedConfig ? (
          // 输入配置文本阶段
          <Form className='studio-import-site-modal__form'>
            <Form.Field>
              <label
                style={{
                  fontSize: '14px',
                  fontWeight: '600',
                  marginBottom: '8px',
                }}
              >
                分组名称
              </label>
              <input
                type='text'
                value={groupName}
                onChange={(e) => setGroupName(e.target.value)}
                placeholder='default'
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  borderRadius: '8px',
                  border: '2px solid rgba(0,0,0,0.12)',
                  fontSize: '14px',
                  outline: 'none',
                  transition: 'border-color 0.2s',
                }}
                onFocus={(e) => (e.target.style.borderColor = '#667eea')}
                onBlur={(e) =>
                  (e.target.style.borderColor = 'rgba(0,0,0,0.12)')
                }
              />
              <div
                style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}
              >
                导入的渠道将分配到该分组，留空或填 default 为默认分组
              </div>
            </Form.Field>
            <Form.Field>
              <label
                style={{
                  fontSize: '14px',
                  fontWeight: '600',
                  marginBottom: '8px',
                }}
              >
                渠道类型
              </label>
              <Dropdown
                selection
                search
                value={channelType}
                options={CHANNEL_OPTIONS}
                onChange={(e, { value }) => setChannelType(value)}
                style={{
                  width: '100%',
                  borderRadius: '8px',
                }}
              />
              <div
                style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}
              >
                选择导入渠道的类型，默认为自定义渠道
              </div>
            </Form.Field>
            <Form.Field>
              <label
                style={{
                  fontSize: '14px',
                  fontWeight: '600',
                  marginBottom: '8px',
                }}
              >
                请粘贴站点配置文本
              </label>
              <TextArea
                value={configText}
                onChange={(e) => setConfigText(e.target.value)}
                placeholder={`示例格式：
# Banana API 公益站 
Base URL: \`https://api.banana2556.com/\` 

## 绑定凭证 (2) 
- test@example.com 
  备注: test123
  api_key: sk-xxxxx

## 支持模型 (3) 
- gpt-4
- gpt-3.5-turbo
- claude-3-opus`}
                rows={15}
                style={{
                  fontFamily: 'monospace',
                  fontSize: '13px',
                  borderRadius: '8px',
                  border: '2px solid rgba(0,0,0,0.12)',
                  padding: '12px',
                }}
              />
            </Form.Field>
          </Form>
        ) : (
          // 预览解析结果阶段
          <div className='studio-import-site-modal__preview'>
            <Message success style={{ borderRadius: 12, boxShadow: 'none' }}>
              <Message.Header>
                <Icon name='check circle' /> 解析成功
              </Message.Header>
              <p>站点名称：{parsedConfig.siteName || '未命名'}</p>
              <p>Base URL：{parsedConfig.baseUrl}</p>
              <p>凭证数量：{parsedConfig.credentials.length} 个</p>
              <p>模型数量：{parsedConfig.models.length} 个</p>
            </Message>

            <Message warning style={{ borderRadius: 12, boxShadow: 'none' }}>
              <Message.Header>
                <Icon name='exclamation triangle' /> 覆盖模式
              </Message.Header>
              <p>
                导入将按渠道标题名自动分组，仅删除同名同组的旧渠道后再创建新渠道，
                不会清空 default 或其他大分组。
              </p>
            </Message>

            {/* 一键拉取上游模型区域 */}
            <div
              className='studio-import-site-modal__model-fetch'
              style={{
                marginTop: 20,
                padding: 18,
                background: '#fff',
                borderRadius: 14,
                border: '1px solid rgba(24, 24, 27, 0.09)',
                boxShadow: '0 8px 24px rgba(24, 24, 27, 0.04)',
              }}
            >
              <h4 style={{ marginBottom: '8px', color: '#18181b' }}>
                <Icon name='sync alternate' /> 一键拉取上游模型
              </h4>
              <p
                style={{
                  fontSize: '12px',
                  color: '#6b7280',
                  marginBottom: '12px',
                }}
              >
                从上游 API 拉取所有可用模型，自动随机使用一个凭证的
                Key，拉取结果与现有模型合并去重
              </p>
              <Button
                color='blue'
                onClick={handleFetchModels}
                loading={fetchingModels}
                disabled={fetchingModels}
              >
                <Icon name='cloud download' /> 拉取所有模型
              </Button>
            </div>

            <div
              className='studio-import-site-modal__channel-list'
              style={{ marginTop: 20 }}
            >
              <h4 style={{ marginBottom: 12, color: '#18181b' }}>
                <Icon name='list' /> 待导入渠道列表（共 {channels.length} 个）
              </h4>
              <Table
                compact
                striped
                style={{ borderRadius: 12, overflow: 'hidden' }}
              >
                <Table.Header>
                  <Table.Row>
                    <Table.HeaderCell>渠道名称</Table.HeaderCell>
                    <Table.HeaderCell>Base URL</Table.HeaderCell>
                    <Table.HeaderCell>API Key</Table.HeaderCell>
                    <Table.HeaderCell>模型数量</Table.HeaderCell>
                    <Table.HeaderCell>状态</Table.HeaderCell>
                    <Table.HeaderCell>操作</Table.HeaderCell>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {channels.length === 0 ? (
                    <Table.Row>
                      <Table.Cell
                        colSpan={6}
                        textAlign='center'
                        style={{ padding: 24, color: '#6b7280' }}
                      >
                        <Icon name='inbox' /> 没有可导入的渠道
                      </Table.Cell>
                    </Table.Row>
                  ) : (
                    channels.map((channel, index) => (
                      <Table.Row key={index}>
                        <Table.Cell>{channel.name}</Table.Cell>
                        <Table.Cell>
                          <code style={{ fontSize: '12px' }}>
                            {channel.base_url}
                          </code>
                        </Table.Cell>
                        <Table.Cell>
                          <code style={{ fontSize: '12px' }}>
                            {channel.key.substring(0, 8)}...
                          </code>
                        </Table.Cell>
                        <Table.Cell>
                          <Label size='tiny' color='blue'>
                            {channel.models.split(',').length} 个
                          </Label>
                        </Table.Cell>
                        <Table.Cell>
                          <Label size='tiny' color='green'>
                            <Icon name='plus' /> 待导入
                          </Label>
                        </Table.Cell>
                        <Table.Cell>
                          <Button
                            size='mini'
                            negative
                            basic
                            icon
                            onClick={() => handleRemoveChannel(index)}
                            title='从导入列表移除'
                          >
                            <Icon name='trash' />
                          </Button>
                        </Table.Cell>
                      </Table.Row>
                    ))
                  )}
                </Table.Body>
              </Table>
            </div>

            <div
              className='studio-import-site-modal__models'
              style={{
                marginTop: 20,
                padding: 18,
                border: '1px solid rgba(24, 24, 27, 0.09)',
                borderRadius: 14,
                background: '#fff',
              }}
            >
              <h4 style={{ marginBottom: 12, color: '#18181b' }}>
                <Icon name='cube' /> 支持模型列表（共{' '}
                {parsedConfig.models.length} 个）
              </h4>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {parsedConfig.models.map((model, index) => (
                  <Label key={index} color='purple' size='small'>
                    {model}
                  </Label>
                ))}
              </div>
            </div>
          </div>
        )}
      </Modal.Content>

      <Modal.Actions
        className='studio-import-site-modal__actions'
        style={{
          padding: '16px 24px',
          background: '#ffffff',
          borderTop: '1px solid rgba(24, 24, 27, 0.08)',
        }}
      >
        {!parsedConfig ? (
          <>
            <Button onClick={handleClose} style={{ marginRight: '8px' }}>
              <Icon name='cancel' /> 取消
            </Button>
            <Button
              primary
              onClick={handleParse}
              loading={loading}
              disabled={!configText.trim()}
            >
              <Icon name='search' /> 解析配置
            </Button>
          </>
        ) : (
          <>
            <Button onClick={handleClose} disabled={importing}>
              <Icon name='cancel' /> 取消
            </Button>
            <Button
              onClick={() => {
                setParsedConfig(null);
                setChannels([]);
                setError('');
              }}
              disabled={importing}
            >
              <Icon name='arrow left' /> 返回修改
            </Button>
            <Button
              primary
              onClick={handleImport}
              loading={importing}
              disabled={channels.length === 0}
            >
              <Icon name='check' /> 确认导入 ({channels.length})
            </Button>
          </>
        )}
      </Modal.Actions>
    </Modal>
  );
};

export default ImportSiteModal;
